import { createContext, useContext, useState, useCallback, useRef, type ReactNode } from 'react'
import type { ApiErrorParts } from '@/lib/apiError'

type ToastType = 'success' | 'error' | 'info'

export type ToastInput = string | ApiErrorParts

export interface Toast {
  id: number
  type: ToastType
  /** Simple one-line toasts */
  message: string
  /** App context, e.g. "Save failed" */
  title?: string
  statusCode?: number | null
  /** ProblemDetails `title` from API, e.g. "Bad Request" */
  problemTitle?: string
  detail?: string
  pinned?: boolean
}

interface ToastContextValue {
  toasts: Toast[]
  toast: (input: ToastInput, type?: ToastType) => void
  dismissToast: (id: number) => void
  toggleToastPin: (id: number) => void
}

const ToastContext = createContext<ToastContextValue | null>(null)
let nextId = 0

function isApiErrorParts(input: ToastInput): input is ApiErrorParts {
  return typeof input === 'object' && input !== null && 'detail' in input
}

function autoDismissMsForToast(t: Pick<Toast, 'type' | 'detail'>): number {
  const isStructuredError = t.detail !== undefined
  if (isStructuredError || t.type === 'error') return 12_000
  return 3000
}

export function ToastProvider({ children }: { children: ReactNode }) {
  const [toasts, setToasts] = useState<Toast[]>([])
  const timersRef = useRef<Map<number, ReturnType<typeof setTimeout>>>(new Map())

  const clearTimer = useCallback((id: number) => {
    const existing = timersRef.current.get(id)
    if (existing) clearTimeout(existing)
    timersRef.current.delete(id)
  }, [])

  const scheduleAutoDismiss = useCallback(
    (id: number, ms: number) => {
      clearTimer(id)
      const handle = setTimeout(() => {
        setToasts(p => {
          const current = p.find(x => x.id === id)
          if (current?.pinned) return p
          return p.filter(x => x.id !== id)
        })
        timersRef.current.delete(id)
      }, ms)
      timersRef.current.set(id, handle)
    },
    [clearTimer]
  )

  const dismissToast = useCallback(
    (id: number) => {
      clearTimer(id)
      setToasts(p => p.filter(t => t.id !== id))
    },
    [clearTimer]
  )

  const toggleToastPin = useCallback(
    (id: number) => {
      setToasts(p =>
        p.map(t => {
          if (t.id !== id) return t
          const willPin = !t.pinned
          if (willPin) {
            clearTimer(id)
          } else {
            const ms = autoDismissMsForToast(t)
            queueMicrotask(() => scheduleAutoDismiss(id, ms))
          }
          return { ...t, pinned: willPin }
        })
      )
    },
    [clearTimer, scheduleAutoDismiss]
  )

  const toast = useCallback(
    (input: ToastInput, type: ToastType = 'success') => {
      const id = ++nextId

      if (isApiErrorParts(input)) {
        setToasts(p => [
          ...p,
          {
            id,
            type: 'error',
            message: '',
            title: input.title,
            statusCode: input.statusCode,
            problemTitle: input.problemTitle,
            detail: input.detail,
            pinned: false,
          },
        ])
        scheduleAutoDismiss(id, 12_000)
        return
      }

      const message = input
      const newToast: Toast = { id, message, type, pinned: false }
      setToasts(p => [...p, newToast])
      scheduleAutoDismiss(id, autoDismissMsForToast(newToast))
    },
    [scheduleAutoDismiss]
  )

  return (
    <ToastContext.Provider value={{ toasts, toast, dismissToast, toggleToastPin }}>
      {children}
    </ToastContext.Provider>
  )
}

export function useToast() {
  const ctx = useContext(ToastContext)
  if (!ctx) throw new Error('useToast must be used within ToastProvider')
  return ctx
}
